import { readPreviewTeam } from '../../service/previewStorage';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';
import usePortal from 'react-useportal';
import {
  StyledWrapperContainer,
  StyledLeagueGrid,
  StyledTableColumn,
  StyledFormColumn,
  StyledStatsColumn,
  StyledFixturesColumn,
  StyledHero,
  StyledHeroPhoto,
  StyledHeroCopy,
  StyledStandingsCard,
  StyledPanelCard,
  StyledAllScorersLink,
} from './styled';
import { StyledInfoIcon, StyledReactTooltip } from '../../globalStyled';

import TableContainer from './components/TableContainer';
import CalendarContainer from './components/CalendarContainer';
import LeagueStats from './components/LeagueStats';
import LeagueScorers from './components/LeagueScorers';
import LeagueForm from './components/LeagueForm';
import PlayerCareer from './components/PlayerCareer';
import LeagueSwitcher from '../../components/LeagueSwitcher';
import { PlayerInfoPopup } from '../../components/Popup';
import SectionHeading from '../../components/SectionHeading';
import { ReactComponent as ArrowUpRight } from '../../assets/img/arrow-up-right.svg';
import { matchdayProgress } from '../../service/leagueTable';
import { POPUPS } from '../../service/constants';
import img from '../../assets/img/league-hero.jpg';

import {
  getLeaguesList,
  leagueGetLeagueTableData,
  leagueCreateCalendar,
  getTeamsFromLeague,
  getCalendarFromLeague,
  getPlayerInformation,
} from '../../redux/actions/League';
import { transferGetDataToSearchable } from '../../redux/actions/Transfers';
import { showPopupWindow } from '../../redux/actions/Popups';

import {
  getLeagueId,
  isTeamListLoaded,
  calendarData,
  teamListLoader,
  isLeaguesListLoaded,
  getPlayerInformationSelector,
  leagueTable,
} from '../../redux/selector/League';
import {
  getSearchPlayer,
  getSearchPlayerLoading,
} from '../../redux/selector/Transfers';
import { getPopupByName } from '../../redux/selector/Popups';
import { isAuth, getMyCurrentTeam } from '../../redux/selector/User';
import { getMyAllSquad } from '../../redux/selector/Team';
import { teamGetMyTeam } from '../../redux/actions/Team';

export default function League() {
  const dispatch = useDispatch();
  const location = useLocation();
  const isMatchPage = location.pathname.startsWith('/league/match');
  const [selectedLeague, selectLeague] = React.useState(null);

  const leagueId = useSelector(getLeagueId);
  const rankingFromTable = useSelector(leagueTable);
  const teamListTable = useSelector(isTeamListLoaded);
  const tableRows = !isEmpty(teamListTable)
    ? teamListTable
    : Array.isArray(rankingFromTable)
      ? rankingFromTable
      : [];
  const fixturesData = useSelector(calendarData);
  const currentTeamData = useSelector(getMyCurrentTeam);
  const isAuthenticated = useSelector(isAuth);
  const squad = useSelector(getMyAllSquad);
  const areTeamsLoaded = useSelector(teamListLoader);
  const leagueOptionsList = useSelector(isLeaguesListLoaded);
  const leaguePlayers = useSelector(getSearchPlayer);
  const leaguePlayersLoading = useSelector(getSearchPlayerLoading);
  const playerInfoPopupData = useSelector(
    getPopupByName(POPUPS.PLAYER_INFO_POPUP)
  );
  const playerInfoData = useSelector(getPlayerInformationSelector);

  const { Portal } = usePortal({
    bindTo: document && document.querySelector('#modal'),
  });

  const selectLeagueFromList = React.useCallback(
    (leagueOption) => {
      if (leagueOption !== selectedLeague) {
        dispatch(getTeamsFromLeague(leagueOption));
        dispatch(getCalendarFromLeague(leagueOption));
        selectLeague(leagueOption);
      }
    },
    [dispatch, selectedLeague]
  );

  useEffect(() => {
    dispatch(leagueGetLeagueTableData());
    dispatch(transferGetDataToSearchable());
    dispatch(teamGetMyTeam());
    dispatch(getLeaguesList());
  }, [dispatch]);

  useEffect(() => {
    if (leagueId && !selectedLeague) {
      selectLeague(leagueId);
    }
  }, [leagueId, selectedLeague]);

  useEffect(() => {
    if (!fixturesData.currentDate) {
      dispatch(leagueCreateCalendar());
    }
  }, [dispatch, fixturesData.currentDate]);

  const hasFixtureDays =
    Array.isArray(fixturesData?.matches) && fixturesData.matches.length > 0;
  const fixturesLoading = !hasFixtureDays;

  const previewTeamData = readPreviewTeam();
  const myTeamId = isAuthenticated ? currentTeamData?._id : previewTeamData?.id;
  const selectedLeagueName = Array.isArray(leagueOptionsList)
    ? leagueOptionsList.find((item) => item._id === selectedLeague)?.name
    : undefined;
  const { current: matchdayCurrent, total: matchdayTotal } = useMemo(
    () =>
      matchdayProgress(fixturesData?.matches, {
        currentDate: fixturesData?.currentDate,
        timezone: fixturesData?.timezone,
      }),
    [fixturesData]
  );

  const openPlayer = (playerId) => {
    dispatch(
      showPopupWindow(POPUPS.PLAYER_INFO_POPUP, POPUPS.PLAYER_INFO_POPUP, true)
    );
    dispatch(getPlayerInformation(playerId, POPUPS.PLAYER_INFO_POPUP));
  };

  return (
    <StyledWrapperContainer>
      {!isEmpty(playerInfoPopupData) && (
        <Portal>
          <PlayerInfoPopup
            popupName={POPUPS.PLAYER_INFO_POPUP}
            playerInfoData={playerInfoData}
          />
        </Portal>
      )}
      {!isMatchPage ? (
        <StyledHero>
          <StyledHeroPhoto>
            <img src={img} alt="" />
          </StyledHeroPhoto>
          <StyledHeroCopy>
            <h1>
              The League
              <StyledInfoIcon
                iconsize="14"
                data-tip
                clickable="true"
                data-for="league-tooltip"
              />
              <StyledReactTooltip
                id="league-tooltip"
                type="light"
                effect="solid"
                place="bottom"
              >
                THE LEAGUE is our regular professional tournament. Ten teams play
                each other nightly over the course of 18 days. A win earns 3
                points, a draw is 1 point and a loss means no point
              </StyledReactTooltip>
            </h1>
            {matchdayCurrent != null && matchdayTotal != null ? (
              <p>
                Matchday {matchdayCurrent} / {matchdayTotal}
              </p>
            ) : null}
          </StyledHeroCopy>
          {!isEmpty(leagueOptionsList) ? (
            <LeagueSwitcher
              leagues={leagueOptionsList}
              value={selectedLeague}
              onChange={selectLeagueFromList}
              disabled={areTeamsLoaded}
            />
          ) : null}
        </StyledHero>
      ) : null}
      <Switch>
        <Route path="/league/scorers">
          <LeagueScorers
            players={leaguePlayers}
            squad={squad}
            myTeam={currentTeamData}
            leagueId={selectedLeague}
            teams={tableRows}
            isLoading={leaguePlayersLoading}
            onSelectPlayer={openPlayer}
          />
        </Route>
        <Route path="/league/career/:playerId" component={PlayerCareer} />
        <Route exact path="/league">
          <StyledLeagueGrid>
            <StyledTableColumn>
              <SectionHeading title="League" />
              <StyledStandingsCard>
                <TableContainer
                  leagueId={leagueId}
                  table={tableRows}
                  selectedLeague={selectedLeague}
                  leagueName={selectedLeagueName}
                />
              </StyledStandingsCard>
            </StyledTableColumn>
            <StyledFixturesColumn>
              <SectionHeading title="Fixtures" />
              <CalendarContainer
                fixturesData={fixturesData}
                teamId={myTeamId}
                isLoading={fixturesLoading}
              />
            </StyledFixturesColumn>
            <StyledFormColumn>
              <SectionHeading title="Form" />
              <StyledPanelCard>
                <LeagueForm
                  teams={tableRows}
                  fixtures={fixturesData?.matches}
                  myTeamId={myTeamId}
                  isLoading={!hasFixtureDays}
                />
              </StyledPanelCard>
            </StyledFormColumn>
            <StyledStatsColumn>
              <SectionHeading
                title="Scorers"
                action={
                  <StyledAllScorersLink to="/league/scorers">
                    All scorers
                    <ArrowUpRight />
                  </StyledAllScorersLink>
                }
              />
              <StyledPanelCard>
                <LeagueStats
                  players={leaguePlayers}
                  squad={squad}
                  myTeam={currentTeamData}
                  leagueId={selectedLeague}
                  teams={tableRows}
                  isLoading={leaguePlayersLoading}
                  onSelectPlayer={openPlayer}
                />
              </StyledPanelCard>
            </StyledStatsColumn>
          </StyledLeagueGrid>
        </Route>
      </Switch>
    </StyledWrapperContainer>
  );
}
